import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { WebSocketMessage } from '../types/alerts';

interface UseWebSocketOptions {
  url?: string;
  authToken?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error';
  permanentlyDisconnected: boolean;
  sendMessage: (message: WebSocketMessage) => void;
  lastMessage: WebSocketMessage | null;
  reconnect: () => void;
}

export const useWebSocket = (
  onMessage: (message: WebSocketMessage) => void,
  options: UseWebSocketOptions = {}
): UseWebSocketReturn => {
  /**
   * Constructs the WebSocket URL following the same pattern as API URLs
   * @returns Complete WebSocket URL for the connection
   */
const getWebSocketUrl = (): string => {
  if (process.env.NODE_ENV === 'production') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }

  // In development, check if we're running through IIS (port 8000) or direct (port 3000)
  if (window.location.port === '8000') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }

  // Direct React dev server - connect directly to backend
  return 'http://localhost:10000';
};

  /**
   * Determines the forwarded host for multitenant support
   * Follows the same pattern used in AlertApiService
   * @param socketUrl - The WebSocket URL to extract host from
   * @returns Host string for x-forwarded-host header
   */
  const getForwardedHost = (socketUrl: string): string => {
    let forwardedHost = "";
    try {
      const parsedUrl = socketUrl.startsWith("http")
        ? new URL(socketUrl)
        : new URL(socketUrl, window.location.origin);
      forwardedHost = parsedUrl.host;
    } catch (error) {
      forwardedHost = window.location.host;
    }
    return forwardedHost;
  };

  const {
    url = getWebSocketUrl(),
    authToken,
    reconnectInterval = 3000,
    maxReconnectAttempts = 50
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [permanentlyDisconnected, setPermanentlyDisconnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  
  const socketRef = useRef<Socket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('message', message);
    } else {
      console.warn('Socket.IO is not connected. Message not sent:', message);
    }
  }, []);

  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    setConnectionState('connecting');

    try {
      const forwardedHost = getForwardedHost(url);

      const socket = io(url, {
        transports: ['websocket', 'polling'],
        extraHeaders: {
          'x-forwarded-host': forwardedHost
        },
        auth: authToken ? { token: authToken } : undefined,
        reconnection: false
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        console.log('Socket.IO connected:', socket.id);
        setIsConnected(true);
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;

        // Join tenant-specific room so we only receive alerts for this tenant
        socket.emit('join-tenant', forwardedHost);
      });

      // Listen for alert_update events from your backend
      socket.on('alert_update', (data) => {
        console.log('Received alert_update:', data);
        const message: WebSocketMessage = {
          type: 'alert_update',
          payload: data, // Keep full object with component, action, and alert fields
          timestamp: new Date().toISOString()
        };
        setLastMessage(message);
        onMessage(message);
      });

      // Listen for generic messages
      socket.on('message', (data) => {
        try {
          const message: WebSocketMessage = typeof data === 'string' ? JSON.parse(data) : data;
          setLastMessage(message);
          onMessage(message);
        } catch (error) {
          console.error('Error parsing Socket.IO message:', error);
        }
      });

      socket.on('disconnect', (reason) => {
        console.log('Socket.IO disconnected:', reason);
        setIsConnected(false);
        setConnectionState('disconnected');

        if (reason !== 'io client disconnect' && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current += 1;

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setPermanentlyDisconnected(true);
        }
      });

      socket.on('connect_error', () => {
        setConnectionState('error');
      });

      socket.on('error', (error) => {
        console.error('Socket.IO error:', error);
        const message: WebSocketMessage = {
          type: 'error',
          error: error.message || 'Socket.IO error',
          timestamp: new Date().toISOString()
        };
        setLastMessage(message);
        onMessage(message);
      });

    } catch {
      setConnectionState('error');
    }
  }, [url, authToken, onMessage, maxReconnectAttempts, reconnectInterval]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setIsConnected(false);
    setConnectionState('disconnected');
    reconnectAttemptsRef.current = 0;
  }, []);

  const reconnect = useCallback(() => {
    setPermanentlyDisconnected(false);
    disconnect();
    setTimeout(connect, 100);
  }, [connect, disconnect]);

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionState,
    permanentlyDisconnected,
    sendMessage,
    lastMessage,
    reconnect
  };
};